import { DetailedHTMLProps, InputHTMLAttributes, forwardRef, useEffect, useRef, useState } from "react";

import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import clsx from "classnames";

export type EditableTableCellProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  label: string;
  error?: string;
  help?: string;
  helpTooltip?: string;
  className?: string;
  "data-testid"?: string;
  onEscPressed?: () => void;
  submit?: () => void;
  autoFocusOnEdit?: boolean;
};

// eslint-disable-next-line react/display-name
export const EditableTableCell = forwardRef<HTMLInputElement, EditableTableCellProps>(
  (
    {
      label,
      className: classNameProps,
      "data-testid": dataTestId = "editable-table-cell",
      error,
      help,
      helpTooltip,
      onEscPressed,
      submit,
      disabled,
      autoFocusOnEdit = false,
      ...inputProps
    },
    forwardedRef
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isEscPressed, setIsEscPressed] = useState(false);

    const inputRef = useRef<HTMLInputElement>();

    useEffect(() => {
      if (autoFocusOnEdit || isFocused) {
        inputRef.current?.focus();
      }
    }, [autoFocusOnEdit, isFocused]);

    const className = clsx(
      "relative",
      "block",
      "w-full",
      "border",
      "whitespace-nowrap",
      "overflow-hidden",
      "text-ellipsis",
      "focus:rounded-md",
      "hover:rounded-md",
      "border-solid",
      "border-transparent",
      "outline-hidden",
      "hover:outline-hidden",
      "focus:outline-hidden",
      "placeholder-neutral-400",
      "placeholder-text-sm",
      "focus:border-platform-600",
      "hover:border-platform-600",
      error && "text-red-700 bg-negative-500/10 hover:bg-negative-500/20"
    );

    useEffect(() => {
      const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Escape" && isFocused) {
          onEscPressed?.();
          setIsEscPressed(true);
          inputRef.current?.blur();
        }
        if (event.key === "Enter" && isFocused && submit) {
          inputRef.current?.blur();
        }
      };

      document.addEventListener("keydown", handleKeyPress);

      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }, [onEscPressed, isFocused, submit]);

    useEffect(() => {
      const handleBlur = () => {
        if (isEscPressed) {
          setIsEscPressed(false);
          return;
        }
        if (!error && isFocused) {
          submit?.();
        }
      };

      inputRef.current?.addEventListener("blur", handleBlur);

      return () => {
        inputRef.current?.removeEventListener("blur", handleBlur);
      };
    }, [error, isEscPressed, isFocused, onEscPressed, submit]);

    const ref = (element: HTMLInputElement) => {
      inputRef.current = element;
      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else if (forwardedRef && typeof forwardedRef === "object") {
        forwardedRef.current = element;
      }
    };

    const focusInput = (focus: boolean) => {
      if (!disabled) {
        focus && inputRef.current?.focus();
        setIsFocused(focus);
      }
    };

    return (
      <div
        className={className}
        role="textbox"
        tabIndex={0}
        onFocus={() => focusInput(true)}
        onBlur={() => focusInput(false)}
        onClick={() => focusInput(true)}
      >
        <input
          ref={ref}
          {...inputProps}
          className={clsx("w-full", classNameProps)}
          data-testid={dataTestId}
          type="text"
          title={label}
          aria-label={label}
          disabled={disabled}
          onFocus={() => {
            !disabled && setIsFocused(true);
          }}
          onBlur={() => !disabled && setIsFocused(false)}
        />
        {Boolean(error) && (
          <span
            data-testid={`${dataTestId}-error-message`}
            className="absolute mt-1 text-xs text-red-700 left-6 bottom-2"
          >
            {error}
          </span>
        )}
        {!error && (help || helpTooltip) && (
          <div
            className={clsx(
              "mt-1",
              "text-xs",
              "absolute",
              "right-0.5",
              "bottom-0.5",
              "flex",
              "flex-row",
              "items-center",
              "gap-1",
              !help && "justify-end"
            )}
          >
            {help && (
              <span
                className={clsx("overflow-hidden", "text-ellipsis", "whitespace-nowrap", "pointer-events-none")}
                data-testid={`${dataTestId}-help-message`}
              >
                {help}
              </span>
            )}
            {helpTooltip && (
              <Tooltip.Wrapper>
                <Tooltip.Trigger>
                  <i className="text-lg text-red-700 cursor-pointer icon-lt-error-warning-line" />
                </Tooltip.Trigger>
                <Tooltip.Content align="center" side="bottom" theme={Theme.Dark} size="sm">
                  <span className="whitespace-normal">{helpTooltip}</span>
                </Tooltip.Content>
              </Tooltip.Wrapper>
            )}
          </div>
        )}
      </div>
    );
  }
);
